import { EntityExtensions } from "@common/ADAPT.Common.Model/entity-extensions.decorator";
import { TeamExtensions } from "@common/ADAPT.Common.Model/organisation/team-extensions";
import { TeamSupplementaryData } from "@common/ADAPT.Common.Model/organisation/team-supplementary-data";
import { TeamType, TeamTypeMetadata } from "@common/ADAPT.Common.Model/organisation/team-type";
import { TeamTypeCode } from "@common/ADAPT.Common.Model/organisation/team-type-code";
import { ActiveEntityUtilities } from "@common/lib/data/active-entity-utilities";
import { BreezeModelUtilities } from "@common/lib/data/breeze-model-utilities";
import { IEntityWithLayout } from "@common/ux/layout/layout.interface";
import { BaseActiveEntity } from "../base-active-entity";
import { BreezeModelBuilder } from "../breeze-model-builder";
import { Person } from "../person/person";
import { Board } from "./board";
import { IEntityWithLabelLocations } from "./entity-with-label-locations";
import { LabelLocation } from "./label-location";
import { Layout } from "./layout";
import { Meeting } from "./meeting";
import { Organisation } from "./organisation";
import { Role } from "./role";
import { RoleConnection } from "./role-connection";
import { RoleTypeCode } from "./role-type-code";
import { TeamLocation } from "./team-location";
import { TeamMeetingGuidance } from "./team-meeting-guidance";
import { TeamTypeConfiguration } from "./team-type-configuration";

export interface ITeamCustomData {
    icon?: string;
    iconColour?: string;
    backgroundColour?: string;
}

export class Team extends BaseActiveEntity<Team> implements IEntityWithLayout, IEntityWithLabelLocations {
    public teamId!: number;
    public organisationId!: number;
    public teamTypeConfigurationId!: number;
    public name!: string;
    public teamLeaderPersonId?: number;
    public startDate!: Date;
    public isPrivate!: boolean;
    public endDate?: Date;
    public teamType!: TeamType;
    public allowObjectivesTeamRead!: boolean;
    public layoutId!: number | null;
    public customData?: string;

    public boards!: Board[];
    public meetings!: Meeting[];
    public organisation!: Organisation;
    public roleConnections!: RoleConnection[];
    public roles!: Role[];
    public teamLeaderPerson!: Person;
    public teamLocations!: TeamLocation[];
    public childTeamLocations!: TeamLocation[];
    public teamMeetingGuidance!: TeamMeetingGuidance;
    public teamTypeConfiguration!: TeamTypeConfiguration;
    public supplementaryData!: TeamSupplementaryData;
    public labelLocations!: LabelLocation[];
    public layout!: Layout | null;

    @EntityExtensions(TeamExtensions)
    public extensions!: TeamExtensions;

    public readonly iconClass = "fal fa-fw fa-users";

    public readonly isInactive = () => ActiveEntityUtilities.isInactiveNow(this);
    public readonly isHistoric = () => ActiveEntityUtilities.isHistoric(this);
    public readonly isNotHistoric = () => ActiveEntityUtilities.isNotHistoric(this);

    public get teamRoleTypeCode() {
        switch (this.teamType) {
            case TeamType.Leadership:
                return RoleTypeCode.Leadership;
            case TeamType.Owner:
                return RoleTypeCode.Owner;
            default:
                return undefined;
        }
    }

    public get teamTypeName() {
        return this.teamTypeConfiguration?.name
            ?? TeamTypeMetadata.Name[this.teamType];
    }

    public get isKeyFunction() {
        return this.teamTypeConfiguration?.code === TeamTypeCode.KeyFunction;
    }

    public get isValueStream() {
        return this.teamTypeConfiguration?.code === TeamTypeCode.ValueStream;
    }
}

export const TeamBreezeModel = new BreezeModelBuilder("Team", Team)
    .hasSource()
    .orderBy("name")
    .isOrganisationEntity()
    .withIdField("teamId")
    .withLabelField("name")
    .withSortField("name")
    .withPropertyValidator("name", BreezeModelUtilities.NonEmptyStringPropertyValidator)
    .instanceIsActivePath()
    .build();
